// Simple User Data Manager
// Basic caching for user profiles without complex preloading

import { simpleCache } from './simpleCache';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

interface CachedUser {
  id: string;
  name: string;
  age: number;
  bio: string;
  photoURL: string;
  photos?: string[];
  height?: string;
  position?: string;
  tribes?: string[];
  lastSeen?: Date;
  isOnline?: boolean;
  [key: string]: any;
}

class SimpleUserDataManager {
  // Get user with caching
  public async getUser(userId: string): Promise<CachedUser | null> {
    const cacheKey = `user_${userId}`;

    // Check cache first
    const cached = simpleCache.get<CachedUser>(cacheKey);
    if (cached) {
      return cached;
    }

    // Fetch from Firebase if not cached
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (!userDoc.exists()) {
        return null;
      }

      const data = userDoc.data();
      const user: CachedUser = {
        id: userDoc.id,
        name: data.displayName || 'Anonymous',
        age: data.age || 25,
        bio: data.bio || 'No bio yet',
        photoURL: data.photoURL || `https://picsum.photos/400/600?random=${userDoc.id}`,
        photos: data.photos || [data.photoURL],
        height: data.height || "5'10\"",
        position: data.position || 'Vers',
        tribes: data.tribes || [],
        lastSeen: data.lastSeen?.toDate() || new Date(),
        isOnline: data.isOnline || false,
        ...data
      };

      // Cache for 5 minutes
      simpleCache.set(cacheKey, user, 300000);

      return user;
    } catch (error) {
      console.warn('Failed to fetch user:', error);
      return null;
    }
  }

  // Cache user data (for when we already have it)
  public cacheUser(user: CachedUser, ttl: number = 300000): void {
    const cacheKey = `user_${user.id}`;
    simpleCache.set(cacheKey, user, ttl);
  }

  // Check if user is cached
  public isUserCached(userId: string): boolean {
    const cacheKey = `user_${userId}`;
    return simpleCache.has(cacheKey);
  }

  // Pre-cache multiple users (from grid data)
  public cacheMultipleUsers(users: CachedUser[], ttl: number = 300000): void {
    users.forEach(user => {
      this.cacheUser(user, ttl);
    });
  }

  // Get cached user only (no fetch)
  public getCachedUser(userId: string): CachedUser | null {
    const cacheKey = `user_${userId}`;
    return simpleCache.get<CachedUser>(cacheKey);
  }

  // Clear user from cache
  public clearUser(userId: string): void {
    const cacheKey = `user_${userId}`;
    simpleCache.delete(cacheKey);
  }
}

// Create singleton instance
export const userDataManager = new SimpleUserDataManager();

// React hook for user data management
export const useUserDataManager = () => {
  const getUser = (userId: string) => userDataManager.getUser(userId);
  const getCachedUser = (userId: string) => userDataManager.getCachedUser(userId);
  const isUserCached = (userId: string) => userDataManager.isUserCached(userId);
  const cacheUser = (user: CachedUser, ttl?: number) => userDataManager.cacheUser(user, ttl);
  const clearUser = (userId: string) => userDataManager.clearUser(userId);

  return {
    getUser,
    getCachedUser,
    isUserCached,
    cacheUser,
    clearUser
  };
};

export type { CachedUser };
export default userDataManager;